/**
 * MiniEdgeLayer Component
 * WP-DASH-2: Edge path rendering for mini Sankey visualization
 *
 * Renders curved paths connecting nodes in the mini Sankey diagram.
 * Uses cubic Bezier curves for smooth flow appearance.
 */

import { memo, useMemo } from 'react';
import type { FlowEdge } from '@/components/flow/types';
import type { Position } from './miniSankeyUtils';
import {
  computeCubicBezierPath,
  computeEdgeStrokeWidth,
  getEdgeColor,
  filterVisibleEdges,
  EDGE_STYLING,
} from './miniSankeyUtils';

// ============================================================================
// Types
// ============================================================================

interface MiniEdgeLayerProps {
  edges: FlowEdge[];
  nodePositions: Map<string, Position>;
}

// ============================================================================
// Component
// ============================================================================

/**
 * MiniEdgeLayer - Renders all edges in the mini Sankey diagram
 *
 * Filters to only render edges between visible nodes and computes
 * curved paths with proportional stroke widths.
 */
function MiniEdgeLayerComponent({ edges, nodePositions }: MiniEdgeLayerProps) {
  // Filter to only edges connecting visible nodes
  const visibleEdges = useMemo(
    () => filterVisibleEdges(edges, nodePositions),
    [edges, nodePositions]
  );

  // Find max value for proportional stroke widths
  const maxValue = useMemo(
    () => Math.max(...visibleEdges.map((e) => e.value), 1),
    [visibleEdges]
  );

  // Pre-compute paths and stroke widths
  const edgeData = useMemo(() => {
    return visibleEdges.map((edge) => {
      const sourcePos = nodePositions.get(edge.source);
      const targetPos = nodePositions.get(edge.target);

      if (!sourcePos || !targetPos) {
        return null;
      }

      return {
        edge,
        path: computeCubicBezierPath(sourcePos, targetPos),
        strokeWidth: computeEdgeStrokeWidth(edge.value, maxValue),
        color: getEdgeColor(edge),
      };
    }).filter(Boolean) as Array<{
      edge: FlowEdge;
      path: string;
      strokeWidth: number;
      color: string;
    }>;
  }, [visibleEdges, nodePositions, maxValue]);

  if (edgeData.length === 0) {
    return null;
  }

  return (
    <g className="mini-edge-layer">
      {edgeData.map(({ edge, path, strokeWidth, color }) => (
        <path
          key={`${edge.source}-${edge.target}`}
          d={path}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeOpacity={EDGE_STYLING.opacity}
          className="transition-opacity duration-150 hover:!stroke-opacity-70"
          aria-label={`Flow from ${edge.source} to ${edge.target}: $${edge.value.toLocaleString()}`}
        />
      ))}
    </g>
  );
}

export const MiniEdgeLayer = memo(MiniEdgeLayerComponent);

export default MiniEdgeLayer;
